import React, { useEffect, useState } from "react";
import {
  FaEnvelope,
  FaPhone,
  FaFacebook,
  FaMapMarkerAlt,
} from "react-icons/fa";
import emailjs from "@emailjs/browser";
import { Helmet } from "react-helmet";
import { db } from "../firebase"; // ✅ import Firestore
import { doc, getDoc } from "firebase/firestore";

import "../styles/Contact.css";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

function formatPhoneNumber(number) {
  const cleaned = ('' + number).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : number;
}

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    website: "",
  });

  const [contactInfo, setContactInfo] = useState(null); 
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    async function fetchContactInfo() {
      try {
        const docRef = doc(db, "settings", "basicInfo");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setContactInfo(docSnap.data());
        } else {
          console.warn("No contact info found");
        }
      } catch (err) {
        console.error("Failed to load contact info:", err);
      }
    }
    fetchContactInfo();
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.website) return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;

    const templateParams = {
      from_name: form.name,
      reply_email: form.email.trim(),
      phone_number: form.phone.trim(),
      message: form.message,
      email: contactInfo?.notificationEmails?.join(", ") || "",
      phone_row_style: form.phone.trim() ? "" : "display:none;",
      email_row_style: form.email.trim() ? "" : "display:none;",
    };

    setSending(true);
    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "", website: "" });
      setTimeout(() => setSent(false), 3500);
    } catch (err) {
      console.error("EmailJS error:", err);
      alert("Sorry, we could not send your message. Please try again later.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="contact-section">
      <Helmet>
        <title>Contact DeClerck’s Goldens | Golden Retriever Breeder in Albion NY</title>
        <meta
          name="description"
          content="Get in touch with DeClerck’s Goldens in Albion, NY. Contact Cary DeClerck for Golden Retriever puppies, stud services, and farm visits (by appointment only)."
        />
        <meta property="og:title" content="Contact DeClerck’s Goldens | Golden Retriever Breeder in Albion NY" />
        <meta property="og:description" content="Contact Cary DeClerck, experienced Golden Retriever breeder in Albion, NY. Puppies, stud services, and visits available by appointment." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.declercksgoldens.com/contact" />
        <meta property="og:image" content="https://www.declercksgoldens.com/hero-bg-square.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact DeClerck’s Goldens | Golden Retriever Breeder in Albion NY" />
        <meta name="twitter:description" content="Reach out to DeClerck’s Goldens for Golden Retriever puppies or stud services. Located in Albion, NY. Visits by appointment only." />
        <meta name="twitter:image" content="https://www.declercksgoldens.com/hero-bg-square.png" />
        <link rel="canonical" href="https://www.declercksgoldens.com/contact" />
      </Helmet>

      <div className="contact-card">
        <div className="contact-info">
          <h2 className="contact-title">
            <FaMapMarkerAlt className="contact-main-icon" />
            Get in Touch with DeClerck’s Goldens
          </h2>
          <p className="contact-lead">
            We are happy to help, answer questions, or schedule your visit.
            <br />
            <strong>
              Please note: Visits are{" "}
              <span className="by-appointment">by appointment only</span>.
            </strong>
          </p>

          {/* ✅ Injected contact info */}
          <div className="contact-details">
            {contactInfo?.email && (
              <div>
                <FaEnvelope className="contact-icon" />
                <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
              </div>
            )}
           {contactInfo?.phone && (
            <div>
              <FaPhone className="contact-icon" />
              <a href={`tel:${contactInfo.phone.replace(/\D/g, "")}`}>
                {formatPhoneNumber(contactInfo.phone)}
              </a>
            </div>
            )}

            {contactInfo?.address && (
              <div>
                <FaMapMarkerAlt className="contact-icon" />
                <span>{contactInfo.address}</span>
              </div>
            )}

            {contactInfo?.social?.facebook && (
              <div className="contact-social">
                <a
                  href={contactInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <FaFacebook />
                </a>
              </div>
            )}
          </div>
        </div>

        {/* ✅ Contact form stays unchanged */}
        <div className="contact-form-map">
          <form className="contact-form" onSubmit={handleSubmit}>
            <h3>Send a Message</h3>

            <input type="text" name="website" value={form.website} onChange={handleChange} style={{ display: "none" }} tabIndex={-1} autoComplete="off" />
            <input id="name" name="name" type="text" placeholder="Your Name" autoComplete="name" value={form.name} onChange={handleChange} required />
            <input id="email" name="email" type="email" placeholder="Your Email" autoComplete="email" value={form.email} onChange={handleChange} required />
            <input id="phone" name="phone" type="tel" placeholder="Your Phone (optional)" autoComplete="tel" value={form.phone} onChange={handleChange} />
            <textarea id="message" name="message" rows={4} placeholder="Your Message" value={form.message} onChange={handleChange} required />

            <button type="submit" disabled={sending}>
              {sending ? "Sending..." : sent ? "Message Sent" : "Send Message"}
            </button>

            {sent && <p className="sent-message" role="status">Thanks for reaching out. We will be in touch soon.</p>}
          </form>

          <div className="contact-map">
            <iframe
              title="DeClerck’s Goldens Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2892.019801721544!2d-78.14682838451749!3d43.25990587913714!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d35dede8e1672f%3A0x2fa7a8f99fa8c35e!2s3078%20Gaines%20Rd%2C%20Albion%2C%20NY%2014411!5e0!3m2!1sen!2sus!4v1716664964737!5m2!1sen!2sus"
            />
            <div className="appointment-note">Visits by appointment only</div>
          </div>
        </div>
      </div>
    </section>
  );
}
